/**
 * The client half of mitra-v1.
 *
 * Names here must equal the keys of artifacts/a2ui-catalog-v1.json, which is
 * generated from streaming/catalog.py: test_frame_parity pins the Python
 * side to the JSON, Surface.test the JSON's wire to this. A static list
 * while every entry is small. Upstream's own code-split point was the first
 * component that weighed something — one `next/dynamic` per component there,
 * so a hundred designs cost bundle size on first draw rather than on every
 * load. `next/dynamic` is a Next.js API with no equivalent import in this
 * Vite app; mm-web's own split point, if one is ever needed, is not yet
 * decided. `Withheld` is one sentence in a div and would cost more as a
 * chunk boundary than it does inline.
 */
import { Catalog } from '@a2ui/web_core/v0_9'
import type { ClientCatalog } from '@/features/mitra/api/frames'
import type { ReactComponentImplementation } from '@a2ui/react/v0_9'
import { Arithmetic } from '@/features/mitra/panel/blocks/charts/Arithmetic'
import { Comparison } from '@/features/mitra/panel/blocks/charts/Comparison'
import { Donut } from '@/features/mitra/panel/blocks/charts/Donut'
import { Facts } from '@/features/mitra/panel/blocks/charts/Facts'
import { Gauge } from '@/features/mitra/panel/blocks/charts/Gauge'
import { Grid } from '@/features/mitra/panel/blocks/charts/Grid'
import { Line } from '@/features/mitra/panel/blocks/charts/Line'
import { Readings } from '@/features/mitra/panel/blocks/charts/Readings'
import { Tiles } from '@/features/mitra/panel/blocks/charts/Tiles'
import { Timeline } from '@/features/mitra/panel/blocks/charts/Timeline'
import { Trend } from '@/features/mitra/panel/blocks/charts/Trend'
import { Withheld } from '@/features/mitra/panel/blocks/charts/Withheld'

/**
 * TWELVE, NOT TWENTY-TWO, and the gap is not a hole. The server's catalog
 * holds ten more names — `positions`, `lots`, `quote` and the rest
 * — so the agent can pick a shape for any answer, and those ten are
 * `BlockType`s drawn by their own React in `panel/blocks/shapes/`, one
 * file per shape, reached through `Projection.tsx` and never through a
 * surface. Only what the server COMPOSES from parts reaches the A2UI
 * document, so this list mirrors it exactly and `catalog.test.ts` pins the
 * two.
 *
 * BOTH HALVES OF THAT SENTENCE WERE WRONG UNTIL THE SHAPES WERE SPLIT OUT.
 * It called a subject of ten "those twelve", and it sent a reader looking
 * under `components/embed/` for React that was not there: every one of the
 * ten was a local function inside `Projection.tsx`, so somebody hunting for
 * `Positions.tsx` found nothing and could not tell whether the file or the
 * comment was the mistake. The extraction came afterwards, to make this
 * true.
 *
 * The catalog id does not move for a new component: adding a leaf is a
 * minor, non-breaking change under A2UI's own versioning rule, and both of
 * these are leaves.
 */
export const mitraCatalog = new Catalog<ReactComponentImplementation>('mitra-v1', [
  Arithmetic,
  Comparison,
  Donut,
  Facts,
  Gauge,
  Grid,
  Line,
  Readings,
  Tiles,
  Timeline,
  Trend,
  Withheld,
])

/**
 * What this widget tells the engine it can draw, on every question it asks.
 *
 * THE ENGINE CHOOSES THE COMPONENT, WHICH IS WHY THIS HAS TO BE SENT. The
 * server resolves a shape from the rows with no model and no client in the
 * loop, so a component added to `CATALOG` after this bundle shipped arrives
 * at a renderer that has never heard of it, and A2UI's answer to an unknown
 * component is to draw nothing, which on this surface is a missing card with
 * no error beside it. `AskRequest.catalog` in the engine's api/app.py is
 * where this lands; a request that omits it is not narrowed at all, so an
 * older widget behaves exactly as it always did.
 *
 * Derived from the catalogue rather than written out. A hand-kept list would
 * be the fourth copy of these names, and the one that says "I can draw this"
 * is the worst of the four to have drift.
 *
 * The id travels with it because a component name means nothing outside the
 * catalogue that defines it: `mitra-v2` sharing the word `Grid` is exactly
 * what `catalogId` exists to keep apart.
 */
export function declaredCatalog(): ClientCatalog {
  return {
    catalog_id: mitraCatalog.id,
    components: [...mitraCatalog.components.keys()],
  }
}
